import type { DocStatus, DocType, PaymentMethod, PaymentSource } from '../api/types';
import { round2 } from './format';

/** Texty a chování typů dokladů 1:1 z prototypu. */
export const TYPES: Record<
  DocType,
  {
    label: string;
    plural: string;
    acc: string;
    done: string;
    title: (vatPayer: boolean) => string;
    vs: string;
    pohoda: boolean;
  }
> = {
  FV: {
    label: 'Faktura',
    plural: 'Faktury',
    acc: 'fakturu',
    done: 'vystavena',
    title: (v) => (v ? 'Faktura – daňový doklad' : 'Faktura'),
    vs: '',
    pohoda: true,
  },
  ZF: {
    label: 'Zálohová faktura',
    plural: 'Zálohové faktury',
    acc: 'zálohovou fakturu',
    done: 'vystavena',
    title: () => 'Zálohová faktura',
    vs: '9',
    pohoda: true,
  },
  PF: {
    label: 'Proforma',
    plural: 'Proformy',
    acc: 'proformu',
    done: 'vystavena',
    title: () => 'Proforma faktura',
    vs: '5',
    pohoda: true,
  },
  DB: {
    label: 'Dobropis',
    plural: 'Dobropisy',
    acc: 'dobropis',
    done: 'vystaven',
    title: () => 'Opravný daňový doklad – dobropis',
    vs: '7',
    pohoda: true,
  },
  DD: {
    label: 'Daňový doklad k platbě',
    plural: 'Daňové doklady k platbě',
    acc: 'daňový doklad',
    done: 'vystaven',
    title: () => 'Daňový doklad k přijaté platbě',
    vs: '8',
    pohoda: true,
  },
  DL: {
    label: 'Dodací list',
    plural: 'Dodací listy',
    acc: 'dodací list',
    done: 'vystaven',
    title: () => 'Dodací list',
    vs: '6',
    pohoda: false,
  },
  VR: {
    label: 'Vrubopis',
    plural: 'Vrubopisy',
    acc: 'vrubopis',
    done: 'vystaven',
    title: () => 'Opravný daňový doklad – vrubopis',
    vs: '4',
    pohoda: false,
  },
  NA: {
    label: 'Nabídka',
    plural: 'Nabídky',
    acc: 'nabídku',
    done: 'vystavena',
    title: () => 'Nabídka',
    vs: '',
    pohoda: false,
  },
  OP: {
    label: 'Objednávka přijatá',
    plural: 'Objednávky přijaté',
    acc: 'objednávku',
    done: 'zapsána',
    title: () => 'Potvrzení objednávky',
    vs: '',
    pohoda: false,
  },
  OV: {
    label: 'Objednávka vydaná',
    plural: 'Objednávky vydané',
    acc: 'objednávku',
    done: 'vystavena',
    title: () => 'Objednávka',
    vs: '',
    pohoda: false,
  },
  PR: {
    label: 'Příjemka',
    plural: 'Příjemky',
    acc: 'příjemku',
    done: 'vystavena',
    title: () => 'Příjemka',
    vs: '',
    pohoda: false,
  },
  VY: {
    label: 'Výdejka',
    plural: 'Výdejky',
    acc: 'výdejku',
    done: 'vystavena',
    title: () => 'Výdejka',
    vs: '',
    pohoda: false,
  },
  FP: {
    label: 'Faktura přijatá',
    plural: 'Faktury přijaté',
    acc: 'přijatou fakturu',
    done: 'zapsána',
    title: () => 'Faktura přijatá',
    vs: '',
    pohoda: false,
  },
  ID: {
    label: 'Interní doklad',
    plural: 'Interní doklady',
    acc: 'interní doklad',
    done: 'vystaven',
    title: () => 'Interní doklad',
    vs: '',
    pohoda: false,
  },
};

export const DOC_TYPES: DocType[] = [
  'FV',
  'ZF',
  'PF',
  'DB',
  'VR',
  'DD',
  'DL',
  'NA',
  'OP',
  'OV',
  'PR',
  'VY',
  'FP',
  'ID',
];
/** Typy, které se vystavují ručně (DD vzniká jen ze zálohy). */
export const MANUAL_TYPES: DocType[] = ['FV', 'ZF', 'PF', 'DB', 'VR', 'DL', 'VY', 'ID'];
/** Typy za feature flagem (kap. 58): bez zapnuté funkce se v nabídce neukážou. */
export const FLAGGED_TYPES: Record<string, DocType[]> = {
  sales_orders: ['NA', 'OP'],
  purchasing: ['OV', 'PR', 'FP'],
};
/** Doklady bez platby: bez splatnosti, způsobu úhrady a QR. */
export const NO_PAYMENT_TYPES: DocType[] = ['DL', 'NA', 'OP', 'OV', 'PR', 'VY', 'ID'];
export const isPayable = (t: DocType) => !NO_PAYMENT_TYPES.includes(t) && t !== 'DD';
/** Doklady bez cen na tisku (dodací list). */
export const noPrices = (t: DocType) => t === 'DL';
/** Přijaté doklady: partner je dodavatel. */
export const isReceived = (t: DocType) => t === 'FP' || t === 'OV' || t === 'PR';

export const LINK_LABEL: Record<string, string> = {
  credit_note_of: 'Dobropis k',
  tax_document_of: 'Daňový doklad k',
  invoice_of_proforma: 'Faktura k proformě',
  delivery_of: 'Dodací list k',
  invoice_of_delivery: 'Faktura k dodacímu listu',
  order_of: 'Z objednávky',
  receipt_of: 'Příjemka k objednávce',
  corrects: 'Opravuje',
  related: 'Navazuje na',
};

export const STATUS: Record<DocStatus, string> = {
  draft: 'Koncept',
  sent: 'Odesláno',
  paid: 'Zaplaceno',
  unpaid: 'Nezaplaceno',
  overdue: 'Po splatnosti',
  partial: 'Částečně zaplaceno',
  settled: 'Vráceno',
  refund: 'K vrácení',
  cancelled: 'Stornováno',
  issued: 'Vystaven',
  confirmed: 'Převzato',
};

export const METHODS: Record<PaymentMethod, string> = {
  transfer: 'Bankovní převod',
  cash: 'Hotovost',
  card: 'Platební karta',
  offset: 'Zápočet',
};

/** Anglické popisky typů/stavů (fáze 14); `applyDocLocale` je přepne v místě, ať se nemění 60 volajících. */
const TYPES_EN: Record<
  DocType,
  { label: string; plural: string; acc: string; done: string; title: (v: boolean) => string }
> = {
  FV: {
    label: 'Invoice',
    plural: 'Invoices',
    acc: 'invoice',
    done: 'issued',
    title: (v) => (v ? 'Invoice – tax document' : 'Invoice'),
  },
  ZF: {
    label: 'Advance invoice',
    plural: 'Advance invoices',
    acc: 'advance invoice',
    done: 'issued',
    title: () => 'Advance invoice',
  },
  PF: {
    label: 'Proforma',
    plural: 'Proformas',
    acc: 'proforma',
    done: 'issued',
    title: () => 'Proforma invoice',
  },
  DB: {
    label: 'Credit note',
    plural: 'Credit notes',
    acc: 'credit note',
    done: 'issued',
    title: () => 'Credit note',
  },
  DD: {
    label: 'Tax document',
    plural: 'Tax documents',
    acc: 'tax document',
    done: 'issued',
    title: () => 'Tax document for payment',
  },
  DL: {
    label: 'Delivery note',
    plural: 'Delivery notes',
    acc: 'delivery note',
    done: 'issued',
    title: () => 'Delivery note',
  },
  VR: {
    label: 'Debit note',
    plural: 'Debit notes',
    acc: 'debit note',
    done: 'issued',
    title: () => 'Debit note',
  },
  NA: {
    label: 'Quotation',
    plural: 'Quotations',
    acc: 'quotation',
    done: 'issued',
    title: () => 'Quotation',
  },
  OP: {
    label: 'Order confirmation',
    plural: 'Order confirmations',
    acc: 'order confirmation',
    done: 'issued',
    title: () => 'Order confirmation',
  },
  OV: {
    label: 'Purchase order',
    plural: 'Purchase orders',
    acc: 'purchase order',
    done: 'issued',
    title: () => 'Purchase order',
  },
  PR: {
    label: 'Goods receipt',
    plural: 'Goods receipts',
    acc: 'goods receipt',
    done: 'issued',
    title: () => 'Goods receipt',
  },
  VY: {
    label: 'Goods issue',
    plural: 'Goods issues',
    acc: 'goods issue',
    done: 'issued',
    title: () => 'Goods issue',
  },
  FP: {
    label: 'Supplier invoice',
    plural: 'Supplier invoices',
    acc: 'supplier invoice',
    done: 'recorded',
    title: () => 'Supplier invoice',
  },
  ID: {
    label: 'Internal document',
    plural: 'Internal documents',
    acc: 'internal document',
    done: 'issued',
    title: () => 'Internal document',
  },
} as Record<
  DocType,
  { label: string; plural: string; acc: string; done: string; title: (v: boolean) => string }
>;
const STATUS_EN: Record<DocStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  partial: 'Partially paid',
  settled: 'Refunded',
  refund: 'To refund',
  cancelled: 'Cancelled',
  issued: 'Issued',
  confirmed: 'Confirmed',
};
const METHODS_EN: Record<PaymentMethod, string> = {
  transfer: 'Bank transfer',
  cash: 'Cash',
  card: 'Card',
  offset: 'Offset',
};
const CS_SNAPSHOT = {
  types: Object.fromEntries(
    (Object.keys(TYPES) as DocType[]).map((k) => [k, { ...TYPES[k] }]),
  ) as typeof TYPES,
  status: { ...STATUS },
  methods: { ...METHODS },
};

/** Přepne popisky dokladů podle jazyka aplikace (mutace sdílených map – volá I18nProvider). */
export function applyDocLocale(locale: 'cs' | 'en'): void {
  for (const k of Object.keys(TYPES) as DocType[]) {
    const src = locale === 'en' ? TYPES_EN[k] : CS_SNAPSHOT.types[k];
    if (src) Object.assign(TYPES[k], src);
  }
  Object.assign(STATUS, locale === 'en' ? STATUS_EN : CS_SNAPSHOT.status);
  Object.assign(METHODS, locale === 'en' ? METHODS_EN : CS_SNAPSHOT.methods);
}

export const SOURCE_LABEL: Record<PaymentSource, string> = {
  bank: 'z výpisu',
  import: 'z POHODY',
  manual: 'zadáno ručně',
  offset: 'zápočet',
};

export const OPEN_STATUSES: DocStatus[] = ['unpaid', 'overdue', 'partial', 'refund'];
export const isOpen = (s: DocStatus) => OPEN_STATUSES.includes(s);

export const vsFor = (type: DocType, number: string) =>
  NO_PAYMENT_TYPES.includes(type) || type === 'FP'
    ? ''
    : TYPES[type].vs + number.replace(/\D/g, '');

export const relatedLabel = (type: DocType) =>
  type === 'DD'
    ? 'K zálohové faktuře'
    : type === 'DB'
      ? 'K faktuře'
      : type === 'DL'
        ? 'K dokladu'
        : 'Navazuje na';

export interface TotalsLike {
  quantity: number | string;
  unitPrice: number | string;
  vatRate: number;
}

/** Součty pro náhled a formulář; server počítá znovu a je zdrojem pravdy. */
export function totalsOf(items: TotalsLike[], vatPayer: boolean) {
  const byRate: Record<number, { base: number; vat: number }> = {};
  let base = 0;
  let vat = 0;
  for (const it of items) {
    const b = round2(Number(it.quantity) * Number(it.unitPrice));
    const r = vatPayer ? Number(it.vatRate) : 0;
    const v = round2((b * r) / 100);
    base = round2(base + b);
    vat = round2(vat + v);
    const bucket = byRate[r] ?? { base: 0, vat: 0 };
    byRate[r] = { base: round2(bucket.base + b), vat: round2(bucket.vat + v) };
  }
  return { base, vat, total: round2(base + vat), byRate };
}
